import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import domtoimage from 'dom-to-image';
import { ActiveToast, ToastrService } from 'ngx-toastr';

import { SquadshotService } from '../../services/squadshot/squadshot.service';
import { UserService } from '../../services/user/user.service';
import { StorageService } from '../../services/storage/storage.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { WebhookEvent, WebhookEventNames } from '@sc/types';
import { CalifoneService } from '../../services/califone/califone.service';
import { IdTokenService } from '../../services/id-token/id-token.service';
import { SquadshotReRenderData } from '@sc/types';
import { Show } from '@sc/types';
import { ShowsService } from '../../services/shows/shows.service';
import { Session } from '@sc/types';

@Component({
  selector: 'sc-squadshot',
  templateUrl: './squadshot.component.html',
  styleUrls: ['./squadshot.component.scss'],
})
export class SquadshotComponent implements OnInit, OnDestroy {
  @ViewChild('ssContainer') ssContainer;

  images: Array<string> = [];
  session: Session = null;
  show: Show = null;

  toast: ActiveToast<any>;
  userParticipating = true;

  countdown: number = null;

  subs: Array<Subscription> = [];

  constructor(
    private squadshotService: SquadshotService,
    private userService: UserService,
    private storageService: StorageService,
    private sessionsService: SessionsService,
    private showsService: ShowsService,
    private idTokenService: IdTokenService,
    private analyticsService: AnalyticsService,
    private califoneService: CalifoneService
  ) {}

  ngOnInit() {
    this.setupSquadshot();
    this.setupSquadshotResult();
    this.setupShowAndSession();
  }

  setupShowAndSession() {
    const sessionSub = this.sessionsService.studioSession$.subscribe((session) => {
      this.session = session;
    });

    const studioShowSub = this.sessionsService.studioShow$.subscribe((show) => {
      this.show = show;
    });

    this.subs.push(sessionSub);
    this.subs.push(studioShowSub);
  }

  setupSquadshot() {
    const sub = this.squadshotService.squadshot$.subscribe((squadshot) => {
      if (!squadshot) return;
      if (this.squadshotService.selfParticipating(squadshot) && this.squadshotService.selfInConference()) {
        this.countdown = squadshot.countdown;
      } else {
        this.countdown = null;
      }
    });
    this.subs.push(sub);
  }

  setupSquadshotResult() {
    const sub = this.squadshotService.squadshotResult$.subscribe((squadshotResult) => {
      if (squadshotResult && this.readyToTakeSquadshot(squadshotResult) && this.squadshotService.selfInConference()) {
        this.images = [];
        this.images = Object.values(squadshotResult.participants);

        this.takeSquadshot();
      }
    });
    this.subs.push(sub);
  }

  readyToTakeSquadshot(squadshotResult) {
    if (squadshotResult.squadshot) return false;
    if (squadshotResult.participants) {
      for (const participant in this.squadshotService.squadshot$.value.readyCheck) {
        if (
          this.squadshotService.squadshot$.value.readyCheck[participant] === true &&
          !squadshotResult.participants[participant]
        )
          return false;
      }
      return true;
    } else return false;
  }

  async takeSquadshot(reRenderData: SquadshotReRenderData = null) {
    if (!reRenderData && this.squadshotService.squadshot$.value.initiator !== this.userService.activeUser$.value.uid)
      return;

    await this.idTokenService.getFreshIdToken(); // Refresh the id token for the squadshot process

    const elm = this.ssContainer.nativeElement;
    let time = this.squadshotService.squadshot$.value?.time?.toString();

    if (reRenderData) {
      this.images = [...reRenderData.images];
      this.session = await firstValueFrom(this.sessionsService.getSessionByID(reRenderData.sessionID));
      this.show = await firstValueFrom(this.showsService.getShowByID(this.session.showID));
      if (!this.show.showImg) this.show.showImg = this.showsService.DEFAULT_IMG;
      time = reRenderData.time;
    }

    for (let i = 0; i < this.images.length; i++) {
      this.images[i] = await this.storageService.imageUrlToBase64(this.images[i]);
    }

    const options = {
      style: {
        opacity: 1,
      },
    };

    this.squadshotService.squadshotRenderReady$.next(true);

    return await new Promise((resolve, reject) => {
      domtoimage
        .toPng(elm, options)
        .then(async (dataUrl) => {
          if (time === null || time === undefined) return;
          const url = await this.storageService.uploadImage(
            `Sessions/${this.session.sessionID}/squadshots`,
            `squadshot_${time}.png`,
            dataUrl
          );

          const result = {
            squadshot: url,
          };
          await this.squadshotService.writeSquadshotResult(result, true, reRenderData);

          const event: WebhookEvent = {
            name: WebhookEventNames.SQUADSHOT_TAKEN,
            squadshot: url,
            orgID: this.show.orgID,
            sessionID: this.session.sessionID,
            sessionTitle: this.session.sessionTitle,
            showID: this.show.showID,
            showName: this.show.showName,
            date: new Date(),
          };
          await this.califoneService.emitWebhookEvent(event, this.show.orgID);

          this.squadshotService.squadshotRenderReady$.next(false);
          this.analyticsService.track('captured squadshot', { success: true });

          resolve('Successfully rendered squadshot');
        })
        .catch(async (error) => {
          console.error('Failed to capture screen image: ', error);
          this.squadshotService.squadshotRenderReady$.next(false);
          this.analyticsService.track('captured squadshot', { success: false });
          reject(error);
        });
    });
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
